import React, { useRef } from 'react';
import { motion, useInView } from 'framer-motion';
import { QuoteIcon } from 'lucide-react';
import { useLanguage } from '../../context/LanguageContext';
import { useEscaCopy } from '../../i18n/escaCopy';
import { clientLogos } from '../../data/clientLogos';
export function ClassicProof() {
  const {
    lang
  } = useLanguage();
  const c = useEscaCopy(lang);
  const ref = useRef<HTMLDivElement>(null);
  const isInView = useInView(ref, {
    once: true,
    margin: '-80px'
  });
  return <section className="relative py-20 lg:py-28 bg-white overflow-hidden" data-id="element-752" data-mp="1o3g1x8i19f3n">
      <div className="absolute inset-0 pointer-events-none" data-id="element-753" data-mp="1o1dg70nwy84q">
        <div className="absolute top-1/4 right-0 w-[450px] h-[450px] rounded-full bg-primary-50/40 blur-3xl hidden sm:block" data-id="element-754" data-mp="195nqarflxauq" />
      </div>

      <div className="relative max-w-7xl mx-auto px-4 sm:px-6 lg:px-8" ref={ref} data-id="element-755" data-mp="1e7d4gfy4m4n1">
        <motion.div initial={{
        opacity: 0,
        y: 20
      }} animate={isInView ? {
        opacity: 1,
        y: 0
      } : {}} transition={{
        duration: 0.5
      }} className="text-center mb-14" data-id="element-756" data-mp="2qdazh01ljrf">
          <span className="text-sm font-semibold text-primary-600 tracking-wide uppercase" data-id="element-757" data-mp="1bucuzajtj939">
            {lang === 'EN' ? 'Track record' : 'Досвід у цифрах'}
          </span>
          <h2 className="mt-3 text-3xl sm:text-4xl lg:text-5xl font-extrabold text-navy-900 tracking-tight max-w-4xl mx-auto" data-id="element-758" data-mp="1prrpuusuc92m">
            {c.proof.title}
          </h2>
        </motion.div>

        <dl className="grid grid-cols-2 lg:grid-cols-5 gap-3 sm:gap-4" data-id="element-759" data-mp="1zpxnh07m6t1o">
          {c.proof.stats.map((s, i) => <motion.div key={s.label} initial={{
          opacity: 0,
          y: 20
        }} animate={isInView ? {
          opacity: 1,
          y: 0
        } : {}} transition={{
          duration: 0.5,
          delay: 0.08 + i * 0.07
        }} className="p-5 lg:p-6 rounded-2xl bg-navy-50/70 border border-navy-200 text-center last:col-span-2 lg:last:col-span-1" data-id="element-760" data-mp="1ur4i5v2he8cl" data-mpi={i}>
              <dt className="text-2xl lg:text-4xl font-extrabold text-navy-900 tracking-tight" data-id="element-761" data-mp="2dl9rxzbknxvs">
                {s.value}
              </dt>
              <dd className="mt-2 text-xs sm:text-sm text-navy-500 leading-snug" data-id="element-762" data-mp="1f7cly8usauq3">
                {s.label}
              </dd>
            </motion.div>)}
        </dl>

        {/* Logos */}
        <div className="mt-20" data-id="element-763" data-mp="1ox5oylgv21iq">
          <p className="text-center text-sm font-semibold text-navy-400 tracking-wide uppercase" data-id="element-764" data-mp="1oy7axkzf08f8">
            {c.proof.logosTitle}
          </p>
          <motion.ul initial={{
          opacity: 0
        }} animate={isInView ? {
          opacity: 1
        } : {}} transition={{
          duration: 0.6,
          delay: 0.2
        }} className="mt-8 grid grid-cols-3 sm:grid-cols-4 lg:grid-cols-5 gap-3 sm:gap-4" data-id="element-765" data-mp="1tyuxnugm233n">
            {clientLogos.map(logo => <li key={logo.name} className={`group flex items-center justify-center h-24 px-5 rounded-2xl border border-navy-200 ${logo.bgClass === 'bg-navy-900' ? 'bg-navy-900' : 'bg-white'}`} data-id="element-766" data-mp="8jfgz3mmet3t">
                <img src={logo.logo} alt={logo.name} loading="lazy" className="max-h-10 w-auto object-contain grayscale opacity-60 transition-[filter,opacity] duration-200 ease-out group-hover:grayscale-0 group-hover:opacity-100" data-id="element-767" data-mp="60khkxza0eff" />
              </li>)}
          </motion.ul>
        </div>

        {/* Quotes */}
        <div className="mt-20" data-id="element-768" data-mp="jyit7vm7pwr8">
          <p className="text-center text-sm font-semibold text-navy-400 tracking-wide uppercase" data-id="element-769" data-mp="jy9388163plj">
            {c.proof.quotesTitle}
          </p>
          <div className="mt-8 grid lg:grid-cols-3 gap-5 lg:gap-6" data-id="element-770" data-mp="f1inxmx6sy16">
            {c.proof.quotes.map((q, i) => <motion.figure key={q.author} initial={{
            opacity: 0,
            y: 20
          }} animate={isInView ? {
            opacity: 1,
            y: 0
          } : {}} transition={{
            duration: 0.5,
            delay: 0.25 + i * 0.08
          }} className="flex flex-col p-6 lg:p-7 rounded-2xl bg-navy-50/60 border border-navy-200" data-id="element-771" data-mp="b06x5e2ku6re" data-mpi={i}>
                <QuoteIcon className="w-6 h-6 text-primary-300" data-id="element-772" data-mp="s7oae936royk" />
                <blockquote className="mt-4 text-[0.9375rem] text-navy-700 leading-relaxed" data-id="element-773" data-mp="dhpcm7fjmfjm">
                  {q.quote}
                </blockquote>
                <figcaption className="mt-auto pt-5 text-sm font-semibold text-navy-900" data-id="element-774" data-mp="igjzplkjmq9j">
                  {q.author}
                </figcaption>
              </motion.figure>)}
          </div>
        </div>
      </div>
    </section>;
}